import type {
  Review,
  ReviewControllerState,
  ReviewReply,
  UserReviewGroup
} from './types'
import type {
  CreateReplyInput,
  CreateReviewInput,
  ReviewClient,
  UpdateReplyInput,
  UpdateReviewInput
} from './client'

type Listener = (state: ReviewControllerState) => void

export interface ReviewControllerOptions {
  client: ReviewClient
  contentId: string | number
  pageSize?: number
}

const defaultState: ReviewControllerState = {
  loading: false,
  loadingMore: false,
  submittingReview: false,
  updatingReview: false,
  deletingReview: false,
  submittingReply: false,
  updatingReply: false,
  deletingReply: false,
  error: null,
  reviewGroups: [],
  currentPage: 1,
  lastPage: 1
}

function cloneGroups(groups: UserReviewGroup[]) {
  return groups.map((group) => ({
    ...group,
    reviews: group.reviews.map((review) => ({
      ...review,
      replies: review.replies ? [...review.replies] : []
    }))
  }))
}

function findReview(groups: UserReviewGroup[], reviewId: number) {
  for (const group of groups) {
    const review = group.reviews.find((item) => item.id === reviewId)
    if (review) {
      return { group, review }
    }
  }
  return null
}

function findReply(groups: UserReviewGroup[], replyId: number) {
  for (const group of groups) {
    for (const review of group.reviews) {
      const reply = review.replies?.find((item) => item.id === replyId)
      if (reply) {
        return { group, review, reply }
      }
    }
  }
  return null
}

function removeReply(groups: UserReviewGroup[], replyId: number) {
  for (const group of groups) {
    for (const review of group.reviews) {
      const index = review.replies?.findIndex((item) => item.id === replyId) ?? -1
      if (index >= 0) {
        review.replies?.splice(index, 1)
        return true
      }
    }
  }
  return false
}

export class ReviewController {
  private listeners = new Set<Listener>()
  private state: ReviewControllerState = { ...defaultState }
  private pageSize: number

  constructor(private readonly options: ReviewControllerOptions) {
    this.pageSize = options.pageSize ?? 10
  }

  getState() {
    return this.state
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    listener(this.state)
    return () => this.listeners.delete(listener)
  }

  private setState(patch: Partial<ReviewControllerState>) {
    this.state = {
      ...this.state,
      ...patch
    }
    for (const listener of this.listeners) {
      listener(this.state)
    }
  }

  private setLoadingPatch(page: number) {
    if (page === 1) {
      this.setState({ loading: true, error: null })
      return
    }
    this.setState({ loadingMore: true, error: null })
  }

  private finishLoading() {
    this.setState({ loading: false, loadingMore: false })
  }

  async loadReviews(page = 1) {
    this.setLoadingPatch(page)
    try {
      const response = await this.options.client.fetchReviews(
        this.options.contentId,
        {
          page,
          perPage: this.pageSize
        }
      )

      // Backend wraps paginator under 'data'; SDK types also allow 'result'
      const envelope = (response as any).data ?? (response as any).result
      const rawItems: any[] = envelope?.data ?? []
      const currentPage: number = envelope?.current_page ?? page
      const lastPage: number = envelope?.last_page ?? page

      // Transform flat Review[] (backend shape) into UserReviewGroup[]
      const nextGroups: UserReviewGroup[] = rawItems.map((item: any) => {
        const user = item.user ?? { id: item.id, name: item.user_name ?? 'Anonymous' }
        const review: Review = {
          ...item,
          user,
          replies: ((item.replies ?? []) as any[]).map((reply: any) => ({
            ...reply,
            user: reply.user ?? { id: reply.id, name: reply.user_name ?? 'Anonymous' }
          }))
        }
        return { user, latest_rating: Number(item.rating ?? 0), reviews: [review] }
      })

      this.setState({
        reviewGroups:
          page === 1
            ? cloneGroups(nextGroups)
            : cloneGroups([...this.state.reviewGroups, ...nextGroups]),
        currentPage,
        lastPage,
        error: null
      })
    } catch (error) {
      this.setState({
        error: error instanceof Error ? error.message : 'Failed to load reviews'
      })
      throw error
    } finally {
      this.finishLoading()
    }
  }

  async loadMore() {
    if (this.state.loadingMore || this.state.currentPage >= this.state.lastPage) return
    await this.loadReviews(this.state.currentPage + 1)
  }

  async submitReview(payload: CreateReviewInput) {
    this.setState({ submittingReview: true })
    try {
      return await this.options.client.createReview(payload)
    } finally {
      this.setState({ submittingReview: false })
    }
  }

  async updateReview(reviewId: number, payload: UpdateReviewInput) {
    this.setState({ updatingReview: true })
    try {
      const response = await this.options.client.updateReview(reviewId, payload)
      const result = (response as any).data ?? response.result
      const found = findReview(this.state.reviewGroups, reviewId)
      if (found && result) {
        Object.assign(found.review, result)
      } else if (found) {
        found.review.rating = payload.rating
        found.review.content = payload.content
      }
      this.setState({ reviewGroups: cloneGroups(this.state.reviewGroups) })
      return response
    } finally {
      this.setState({ updatingReview: false })
    }
  }

  async deleteReview(reviewId: number, userEmail: string) {
    this.setState({ deletingReview: true })
    try {
      const response = await this.options.client.deleteReview(reviewId, { user_email: userEmail })
      this.setState({
        reviewGroups: this.state.reviewGroups
          .map((group) => ({
            ...group,
            reviews: group.reviews.filter((review) => review.id !== reviewId)
          }))
          .filter((group) => group.reviews.length > 0)
      })
      return response
    } finally {
      this.setState({ deletingReview: false })
    }
  }

  async markHelpful(reviewId: number, userEmail: string) {
    return this.options.client.markHelpful(reviewId, { user_email: userEmail })
  }

  async markDislike(reviewId: number, userEmail: string) {
    return this.options.client.markDislike(reviewId, { user_email: userEmail })
  }

  async submitReply(payload: CreateReplyInput) {
    this.setState({ submittingReply: true })
    try {
      const response = await this.options.client.createReply(payload)
      const result = (response as any).data ?? response.result
      if (result) {
        const found = findReview(this.state.reviewGroups, payload.parent_id)
        if (found) {
          found.review.replies = found.review.replies ?? []
          found.review.replies.push({
            ...result,
            user: result.user ?? { id: result.id, name: result.user_name ?? 'Anonymous' }
          })
        }
      }
      this.setState({ reviewGroups: cloneGroups(this.state.reviewGroups) })
      return response
    } finally {
      this.setState({ submittingReply: false })
    }
  }

  async updateReply(replyId: number, payload: UpdateReplyInput) {
    this.setState({ updatingReply: true })
    try {
      const response = await this.options.client.updateReply(replyId, payload)
      const result = (response as any).data ?? response.result
      const found = findReply(this.state.reviewGroups, replyId)
      if (found && result) {
        Object.assign(found.reply, result)
      } else if (found) {
        found.reply.content = payload.content
      }
      this.setState({ reviewGroups: cloneGroups(this.state.reviewGroups) })
      return response
    } finally {
      this.setState({ updatingReply: false })
    }
  }

  async deleteReply(replyId: number, userEmail: string) {
    this.setState({ deletingReply: true })
    try {
      const response = await this.options.client.deleteReply(replyId, { user_email: userEmail })
      removeReply(this.state.reviewGroups, replyId)
      this.setState({
        reviewGroups: cloneGroups(this.state.reviewGroups)
      })
      return response
    } finally {
      this.setState({ deletingReply: false })
    }
  }

  replaceGroups(groups: UserReviewGroup[]) {
    this.setState({ reviewGroups: cloneGroups(groups) })
  }

  patchReview(reviewId: number, patch: Partial<Review>) {
    const found = findReview(this.state.reviewGroups, reviewId)
    if (found) {
      Object.assign(found.review, patch)
      this.setState({ reviewGroups: cloneGroups(this.state.reviewGroups) })
    }
  }

  patchReply(replyId: number, patch: Partial<ReviewReply>) {
    const found = findReply(this.state.reviewGroups, replyId)
    if (found) {
      Object.assign(found.reply, patch)
      this.setState({ reviewGroups: cloneGroups(this.state.reviewGroups) })
    }
  }

  async refresh() {
    await this.loadReviews(1)
  }
}

export function createReviewController(options: ReviewControllerOptions) {
  return new ReviewController(options)
}
